import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "@/components/site/SiteShell";
import LiveCounter from "@/components/site/LiveCounter";
import Testimonials from "@/components/site/Testimonials";
import StoryShowcase from "@/components/site/ScrollStory";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useI18n } from "@/lib/i18n";
import {
    ArrowRight, Sparkles, MessageCircle, Phone, MapPin, QrCode, Images,
    Smartphone, ServerCog, Pencil, Share2, Store, CheckCircle2, Zap,
} from "lucide-react";

const HERO_IMG = "https://customer-assets-4nw71qhi.emergentagent.net/job_education-portal-122/artifacts/8ti2bu4v_image_search_1790851641266.jpg.jpeg";
const EXAMPLE_IMG = "https://images.unsplash.com/photo-1774978238868-786ca6008d97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function HomePage() {
    const { t } = useI18n();

    const STEPS = [
        { icon: Store,     tk: "step1_title", dk: "step1_desc" },
        { icon: Sparkles,  tk: "step2_title", dk: "step2_desc" },
        { icon: Pencil,    tk: "step3_title", dk: "step3_desc" },
        { icon: Share2,    tk: "step4_title", dk: "step4_desc" },
    ];
    const FEATURES = [
        { icon: Smartphone,   tk: "f_mobile_title",  dk: "f_mobile_desc" },
        { icon: MessageCircle, tk: "f_wa_title",     dk: "f_wa_desc" },
        { icon: Phone,         tk: "f_call_title",   dk: "f_call_desc" },
        { icon: MapPin,        tk: "f_map_title",    dk: "f_map_desc" },
        { icon: Images,        tk: "f_gallery_title", dk: "f_gallery_desc" },
        { icon: QrCode,        tk: "f_qr_title",     dk: "f_qr_desc" },
        { icon: ServerCog,     tk: "f_hosted_title", dk: "f_hosted_desc" },
        { icon: Zap,           tk: "f_update_title", dk: "f_update_desc" },
    ];
    const FAQ = [
        ["faq_q6", "faq_a6"], ["faq_q7", "faq_a7"], ["faq_q1", "faq_a1"], ["faq_q2", "faq_a2"], ["faq_q3", "faq_a3"], ["faq_q4", "faq_a4"], ["faq_q5", "faq_a5"],
    ];

    return (
        <SiteShell>
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-14 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
                    <div className="min-w-0">
                        <div className="fade-up inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-[13px] text-muted-foreground">
                            <Sparkles className="w-3.5 h-3.5 text-accent" /> <span className="font-semibold text-foreground">Bizlinkk</span> · {t("home_badge")}
                        </div>
                        <h1 className="fade-up font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.06] tracking-tight text-foreground">
                            {t("home_title_1")} <span className="text-accent">{t("home_title_accent")}</span> {t("home_title_2")}
                        </h1>
                        <p className="fade-up mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">{t("home_sub")}</p>
                        <div className="fade-up mt-8 flex flex-col sm:flex-row gap-3">
                            <Link to="/signup">
                                <Button size="lg" data-testid="hero-create-btn" className="btn-accent rounded-full h-12 px-7 text-base w-full sm:w-auto">
                                    {t("create_website")} <ArrowRight className="ml-1 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/b/royal-garments">
                                <Button size="lg" variant="ghost" data-testid="hero-example-btn" className="glass rounded-full h-12 px-7 text-base w-full sm:w-auto hover:text-accent">
                                    {t("home_cta_see_example")}
                                </Button>
                            </Link>
                        </div>
                        <p className="fade-up mt-6 text-sm text-muted-foreground">{t("home_cta_small")}</p>
                    </div>
                    <div className="fade-up relative">
                        <div className="rounded-3xl overflow-hidden glass-strong aspect-16-9">
                            <img src={HERO_IMG} alt="Business owner using the platform" className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute -bottom-5 -left-3 sm:left-6 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg">
                            <div className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center">
                                <MessageCircle className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold leading-tight">{t("home_new_enquiry")}</div>
                                <div className="text-xs text-muted-foreground">{t("home_new_enquiry_sub")}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Problem / Solution */}
            <section className="relative px-4 sm:px-6 py-14">
                <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5">
                    <div className="glass rounded-3xl p-8">
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("problem_label")}</div>
                        <h3 className="font-heading text-2xl font-semibold mt-3 leading-snug">{t("problem_title")}</h3>
                        <p className="mt-3 text-muted-foreground leading-relaxed">{t("problem_body")}</p>
                    </div>
                    <div className="glass-strong rounded-3xl p-8">
                        <div className="text-xs font-medium uppercase tracking-wide text-accent">{t("solution_label")}</div>
                        <h3 className="font-heading text-2xl font-semibold mt-3 leading-snug">{t("solution_title")}</h3>
                        <p className="mt-3 text-muted-foreground leading-relaxed">{t("solution_body")}</p>
                    </div>
                </div>
            </section>

            {/* Scroll-driven storytelling sections */}
            <StoryShowcase />

            {/* How it works */}
            <section id="how" className="relative px-4 sm:px-6 py-16 scroll-mt-28">
                <div className="max-w-6xl mx-auto">
                    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("how_title")}</h2>
                    <p className="mt-3 text-muted-foreground max-w-2xl">{t("how_sub")}</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 stagger">
                        {STEPS.map(({ icon: Icon, tk, dk }, i) => (
                            <div key={i} className="glass rounded-2xl p-6 hover-lift">
                                <div className="flex items-center justify-between">
                                    <div className="w-10 h-10 rounded-full glass flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-accent" />
                                    </div>
                                    <span className="font-heading text-2xl font-semibold text-muted-foreground/40">{i + 1}</span>
                                </div>
                                <div className="mt-4 font-heading text-lg font-semibold">{t(tk)}</div>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(dk)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Example website */}
            <section className="relative px-4 sm:px-6 py-16">
                <div className="max-w-6xl mx-auto glass-strong rounded-3xl overflow-hidden grid lg:grid-cols-2">
                    <div className="relative min-h-[260px] bg-secondary">
                        <img src={EXAMPLE_IMG} alt="Example business website" className="w-full h-full object-cover absolute inset-0" />
                    </div>
                    <div className="p-8 sm:p-12 flex flex-col justify-center">
                        <div className="text-xs font-medium uppercase tracking-wide text-accent">{t("example_label")}</div>
                        <h2 className="font-heading text-3xl font-semibold mt-3">{t("example_title")}</h2>
                        <p className="mt-3 text-muted-foreground leading-relaxed">{t("example_body")}</p>
                        <Link to="/b/royal-garments" className="mt-6">
                            <Button size="lg" className="btn-accent rounded-full" data-testid="example-visit-btn">
                                {t("example_cta")} <ArrowRight className="ml-1 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="relative px-4 sm:px-6 py-16">
                <div className="max-w-6xl mx-auto">
                    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">{t("features_title")}</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10 stagger">
                        {FEATURES.map(({ icon: Icon, tk, dk }, i) => (
                            <div key={i} className="glass rounded-2xl p-6 hover-lift">
                                <div className="w-10 h-10 rounded-full glass flex items-center justify-center">
                                    <Icon className="w-5 h-5 text-accent" />
                                </div>
                                <div className="mt-4 font-heading text-base font-semibold">{t(tk)}</div>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(dk)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <Testimonials />

            {/* Live counter (decorative, frontend-only) — free, below testimonials */}
            <LiveCounter
                label="Websites being created"
                sub="See how quickly businesses can get online."
            />

            {/* Pricing teaser */}
            <section className="relative px-4 sm:px-6 py-16">
                <div className="max-w-4xl mx-auto glass-strong rounded-3xl p-8 sm:p-12 text-center">
                    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("pricing_teaser_title")}</h2>
                    <p className="mt-3 text-muted-foreground">{t("pricing_teaser_body")}</p>
                    <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm">
                        {["pricing_feature_1","pricing_feature_2","pricing_feature_3","pricing_feature_4"].map((k) => (
                            <span key={k} className="inline-flex items-center gap-1.5 text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t(k)}</span>
                        ))}
                    </div>
                    <Link to="/pricing" className="inline-block mt-8">
                        <Button size="lg" variant="ghost" className="glass rounded-full hover:text-accent" data-testid="pricing-cta-btn">{t("pricing_see_full")} <ArrowRight className="ml-1 w-4 h-4" /></Button>
                    </Link>
                </div>
            </section>

            {/* About Bizlinkk */}
            <section id="about" className="relative px-4 sm:px-6 py-16 scroll-mt-28">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-[13px] text-muted-foreground">About Bizlinkk</div>
                    <h2 className="font-heading mt-6 text-3xl sm:text-4xl font-semibold tracking-tight">What is Bizlinkk?</h2>
                    <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
                        Bizlinkk is a platform that helps offline businesses create a professional online presence
                        through a simple hosted business website. Businesses can share their Bizlinkk link through
                        WhatsApp, Instagram, Facebook, QR codes, visiting cards and other channels.
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section className="relative px-4 sm:px-6 py-12">
                <div className="max-w-3xl mx-auto">
                    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-center">{t("faq_title")}</h2>
                    <div className="mt-8">
                        <Accordion type="single" collapsible className="w-full">
                            {FAQ.map(([q, a], i) => (
                                <AccordionItem key={i} value={`item-${i}`}>
                                    <AccordionTrigger className="text-left">{t(q)}</AccordionTrigger>
                                    <AccordionContent className="text-muted-foreground">{t(a)}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="relative px-4 sm:px-6 py-20">
                <div className="max-w-4xl mx-auto glass-strong rounded-3xl p-10 sm:p-16 text-center overflow-hidden">
                    <h2 className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
                        {t("final_title_1")} <br />
                        <span className="text-accent">{t("final_title_2")}</span>
                    </h2>
                    <p className="mt-5 text-muted-foreground max-w-xl mx-auto">{t("final_body")}</p>
                    <Link to="/signup" className="inline-block mt-8">
                        <Button size="lg" className="btn-accent rounded-full h-12 px-8 text-base" data-testid="final-cta-btn">
                            {t("create_website")} <ArrowRight className="ml-1 w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>
        </SiteShell>
    );
}
